<template>
    <div class="bg-white">
        <div>
            <!-- Mobile filter dialog -->
            <TransitionRoot as="template" :show="mobileFiltersOpen">
                <Dialog class="relative z-40 lg:hidden" @close="mobileFiltersOpen = false">
                    <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                        enter-from="opacity-0" enter-to="opacity-100"
                        leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
                        leave-to="opacity-0">
                        <div class="fixed inset-0 bg-black/25" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-40 flex">
                        <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                            enter-from="translate-x-full" enter-to="translate-x-0"
                            leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                            leave-to="translate-x-full">
                            <DialogPanel
                                class="relative ml-auto flex size-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                                <div class="flex items-center justify-between px-4">
                                    <h2 class="text-lg font-medium text-slate-900">Filters</h2>
                                    <button type="button"
                                        class="-mr-2 flex size-10 items-center justify-center rounded-md bg-white p-2 text-slate-400"
                                        @click="mobileFiltersOpen = false">
                                        <span class="sr-only">Close menu</span>
                                        <XMarkIcon class="size-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <!-- Filters -->
                                <form class="mt-4 border-t border-slate-200">
                                    <h3 class="sr-only">Categories</h3>
                                    <ul role="list" class="px-2 py-3 font-medium text-slate-900">
                                        <li v-for="category in categories" :key="category.name">
                                            <a :href="category.href" class="block px-2 py-3">{{ category.name }}</a>
                                        </li>
                                    </ul>
                                </form>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </TransitionRoot>

            <main class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="flex items-baseline justify-between border-b border-slate-200 pt-24 pb-6">
                    <h1 class="text-4xl font-bold tracking-tight text-slate-900">Courses</h1>

                    <div class="flex items-center">
                        <Menu as="div" class="relative inline-block text-left">
                            <div>
                                <MenuButton
                                    class="group inline-flex justify-center text-sm font-medium text-slate-700 hover:text-slate-900">
                                    Sort
                                    <ChevronDownIcon
                                        class="-mr-1 ml-1 size-5 shrink-0 text-slate-400 group-hover:text-slate-500"
                                        aria-hidden="true" />
                                </MenuButton>
                            </div>

                            <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                                <MenuItems
                                    class="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black/5 focus:outline-hidden">
                                    <div class="py-1">
                                        <MenuItem v-for="option in sortOptions" :key="option.name" v-slot="{ active }">
                                        <a :href="option.href"
                                            :class="[option.current ? 'font-medium text-slate-900' : 'text-slate-500', active ? 'bg-slate-100 outline-hidden' : '', 'block px-4 py-2 text-sm']">{{
                                            option.name }}</a>
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </transition>
                        </Menu>

                        <button type="button" class="-m-2 ml-5 p-2 text-slate-400 hover:text-slate-500 sm:ml-7">
                            <span class="sr-only">View grid</span>
                            <Squares2X2Icon class="size-5" aria-hidden="true" />
                        </button>
                        <button type="button" class="-m-2 ml-4 p-2 text-slate-400 hover:text-slate-500 sm:ml-6 lg:hidden"
                            @click="mobileFiltersOpen = true">
                            <span class="sr-only">Filters</span>
                            <FunnelIcon class="size-5" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                <section aria-labelledby="products-heading" class="pt-6 pb-24">
                    <h2 id="products-heading" class="sr-only">Products</h2>

                    <div class="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4">
                        <!-- Filters -->
                        <form class="hidden lg:block">
                            <h3 class="sr-only">Categories</h3>
                            <ul role="list" class="border-b border-slate-200 pb-6 text-sm font-medium text-slate-900">
                                <li v-for="category in categories" :key="category.name">
                                    <a :href="category.href" class="block py-3 px-3 hover:bg-slate-50">{{ category.name }}</a>
                                </li>
                            </ul>
                        </form>

                        <!-- Product grid -->
                        <div class="lg:col-span-3">
                            <slot name="content"></slot>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    </div>
</template>

<script setup>

import { ref } from 'vue';
import { Dialog, TransitionRoot, TransitionChild } from '@headlessui/vue';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { ChevronDownIcon, FunnelIcon, Squares2X2Icon, XMarkIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
    products: {
        type: Array,
        default: () => []
    },
    categories: {
        type: Array,
        default: () => []
    },
    sortOptions: {
        type: Array,
        default: () => []
    }
});

const mobileFiltersOpen = ref(false);
</script>
